import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { ENV } from '../../../config/env';
import { BrandKitState } from '../BrandKitCreator';

interface Props {
  brandKit: BrandKitState;
  updateBrandKit: (updates: Partial<BrandKitState>) => void;
  onNext?: () => void;
  onBack?: () => void;
}

const ComplimentsSlipTab: React.FC<Props> = ({ brandKit, updateBrandKit, onNext, onBack }) => {
  const { isAuthenticated, getToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [slipHtml, setSlipHtml] = useState('');
  
  // Form fields - pre-filled from brand kit
  const [company, setCompany] = useState(brandKit.businessName || '');
  const [tagline, setTagline] = useState(brandKit.tagline || '');
  const [phone, setPhone] = useState(brandKit.phone || '');
  const [email, setEmail] = useState(brandKit.email || '');
  const [website, setWebsite] = useState(brandKit.website || '');
  const [selectedTemplate, setSelectedTemplate] = useState('elegant');
  
  // Update fields when brand kit changes
  useEffect(() => {
    if (brandKit.businessName && !company) setCompany(brandKit.businessName);
    if (brandKit.tagline && !tagline) setTagline(brandKit.tagline);
    if (brandKit.phone && !phone) setPhone(brandKit.phone);
    if (brandKit.email && !email) setEmail(brandKit.email);
    if (brandKit.website && !website) setWebsite(brandKit.website);
  }, [brandKit]);

  const submit = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to use the Compliments Slip Creator');
      return;
    }
    if (!company) {
      toast.error('Please provide at least a company name');
      return;
    }

    setLoading(true);
    try {
      const userId = await getToken();
      if (!userId) {
        toast.error('Unable to identify user. Please re-login.');
        setLoading(false);
        return;
      }

      const payload = {
        userId,
        company,
        tagline,
        phone,
        email,
        website,
        template: selectedTemplate,
        logoUrl: brandKit.logoDataUrl || brandKit.logoUrl,
        primaryColor: brandKit.primaryColor,
        accentColor: brandKit.accentColor,
      };

      const response = await fetch(`${ENV.API_URL}/api/tools/compliments-slip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Failed to generate compliments slip');
      }

      const data = await response.json();
      setSlipHtml(data.html);
      toast.success('Compliments slip generated!');
    } catch (error) {
      console.error('Compliments slip generation error:', error);
      toast.error('Failed to generate compliments slip. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(slipHtml);
      toast.success('Compliments slip HTML copied to clipboard!');
    } catch (error) {
      const textarea = document.createElement('textarea');
      textarea.value = slipHtml;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      toast.success('Compliments slip HTML copied!');
    }
  };

  const downloadPDF = () => {
    // Create printable version
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>${company} - Compliments Slip</title>
          <style>
            @page { 
              size: A6 landscape; 
              margin: 0; 
            }
            body { margin: 0; padding: 0; }
            @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
          </style>
        </head>
        <body>
          ${slipHtml}
        </body>
        </html>
      `);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Compliments Slip Creator</h2>
          <p className="text-gray-600">
            Professional compliments slip for <span className="font-semibold text-orange-600">{company || 'your business'}</span>
          </p>
        </div>
        {onBack && (
          <button
            onClick={onBack}
            className="px-4 py-2 text-gray-600 hover:text-gray-900 transition"
          >
            ← Back
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form Column */}
        <div className="space-y-6">
          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-1">What is a compliments slip?</p>
                <p>A small note card used to accompany documents, gifts, or packages. It's a professional way to add a personal touch to business communications.</p>
              </div>
            </div>
          </div>

          {/* Template Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Template Style
            </label>
            <select
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="elegant">Elegant (Simple & Clean)</option>
              <option value="modern">Modern (Bold)</option>
              <option value="classic">Classic (Traditional)</option>
              <option value="minimal">Minimal (Text Only)</option>
            </select>
          </div>

          {/* Company Info */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-orange-50"
                placeholder="Company Name"
                readOnly={!!brandKit.businessName}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tagline</label>
              <input
                type="text"
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="Your Business Tagline"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="+1 (555) 123-4567"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="info@company.com"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
              <input
                type="url"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="https://company.com"
              />
            </div>
          </div>

          {/* Generate Button */}
          <button
            onClick={submit}
            disabled={loading || !company}
            className="w-full px-6 py-3 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Generating...' : 'Generate Compliments Slip'}
          </button>
        </div>

        {/* Preview Column */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Preview (A6 Size)</label>
          <div className="border-2 border-gray-200 rounded-lg bg-gray-100 p-8 flex items-center justify-center min-h-[600px]">
            {slipHtml ? (
              <div className="bg-white shadow-lg w-full max-w-md">
                <div dangerouslySetInnerHTML={{ __html: slipHtml }} />
                <div className="p-4 bg-gray-50 border-t border-gray-200 flex gap-2">
                  <button
                    onClick={copyToClipboard}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium"
                  >
                    Copy HTML
                  </button>
                  <button
                    onClick={downloadPDF}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm font-medium"
                  >
                    Print / PDF
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-400">
                <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p>Fill in your details and click Generate</p>
                <p className="text-sm mt-2">to see your compliments slip</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Continue Button */}
      {slipHtml && onNext && (
        <button
          onClick={onNext}
          className="w-full px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition flex items-center justify-center gap-2"
        >
          Continue to Download Kit
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default ComplimentsSlipTab;
